import { type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { ErrorNote } from "@/components/AppShell";
import HouseholdShell from "@/components/HouseholdShell";
import NumericInput from "@/components/NumericInput";
import PostalCodeField from "@/components/PostalCodeField";
import { globalApi } from "@/lib/api";
import type { GeocodeResult, Vessel } from "@/types";

const TYPES = [
  {
    value: "pool",
    label: "Pool",
    detail: "Outdoors and open to the sun, so the overnight chlorine test and the sundown timing "
      + "both apply.",
  },
  {
    value: "hot_tub",
    label: "Hot tub",
    detail: "Under a cover, so there is no UV to reason about. The overnight test and the test-due "
      + "estimate are switched off here — what drives the chlorine is who has been in it.",
  },
] as const;

/**
 * Add a second body of water.
 *
 * Deliberately short. The only fields that change an answer are the type and
 * the volume — volume is the denominator of every dose, and type decides which
 * judgements apply at all (ADR 0015). Everything else is editable afterwards on
 * the Setup screen, so asking for it here would be ceremony in front of the
 * thing the owner actually came to do.
 *
 * The type carries its consequence in the form rather than in a tooltip. Picking
 * "hot tub" silently switches off three of the app's chlorine judgements, and
 * an owner who later wonders where the overnight check went deserves to have
 * been told at the point they chose.
 */
export default function AddVessel() {
  const navigate = useNavigate();
  const [name, setName] = useState("Hot tub");
  const [type, setType] = useState<"pool" | "hot_tub">("hot_tub");
  const [volume, setVolume] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [place, setPlace] = useState<GeocodeResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const created = await globalApi.post<Vessel>("/api/vessels", {
        name: name.trim() || "New pool",
        vessel_type: type,
        volume_gallons: volume,
        postal_code: place?.postal_code ?? (postalCode.trim() || null),
        latitude: place?.latitude ?? null,
        longitude: place?.longitude ?? null,
        ...(place?.timezone ? { timezone: place.timezone } : {}),
        pool_type: type === "hot_tub" ? "above_ground" : "in_ground",
        surface_type: type === "hot_tub" ? "acrylic" : "vinyl_liner",
      });
      // Straight into the thing you just made, rather than back to a list.
      navigate(`/v/${created.id}`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not add it.");
      setBusy(false);
    }
  }

  // A plain shell rather than `AppShell`: this screen is not about any existing
  // vessel, so the bottom nav has no vessel to point at. Rendering it here
  // produced links to `/v/undefined/log`.
  return (
    <HouseholdShell
      title="Add a pool or tub"
      action={
        <Link to="/" className="shrink-0 text-sm text-(--color-ink-muted) underline underline-offset-4">
          Cancel
        </Link>
      }
    >
      {error && <ErrorNote message={error} />}

      <form onSubmit={submit} className="space-y-4">
        <section className="card space-y-4 p-4">
          <div>
            <span className="block text-sm font-medium">What is it?</span>
            <div className="mt-2 space-y-2">
              {TYPES.map((option) => (
                <label
                  key={option.value}
                  className="flex cursor-pointer gap-3 rounded-lg border border-(--color-border-subtle) p-3"
                  style={
                    type === option.value
                      ? { borderColor: "var(--color-brand)" }
                      : undefined
                  }
                >
                  <input
                    type="radio"
                    name="vessel-type"
                    className="mt-1"
                    checked={type === option.value}
                    onChange={() => setType(option.value)}
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">{option.label}</span>
                    <span className="mt-0.5 block text-xs text-(--color-ink-muted)">
                      {option.detail}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="new-name" className="block text-sm font-medium">
              Name
            </label>
            <input
              id="new-name"
              className="field mt-1.5"
              maxLength={120}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="new-volume" className="block text-sm font-medium">
              Volume (gallons)
            </label>
            <p className="mt-0.5 text-xs text-(--color-ink-muted)">
              Every dose divides by this. An inflatable tub's box gives it; most are 250–450.
            </p>
            <NumericInput
              id="new-volume"
              className="field figure mt-1.5"
              value={volume}
              onChange={setVolume}
              required
            />
          </div>

          <PostalCodeField
            value={postalCode}
            onChange={setPostalCode}
            resolved={place}
            onResolved={(next) => {
              setPlace(next);
              setPostalCode(next.postal_code);
            }}
            hasCoordinates={false}
          />
        </section>

        <button type="submit" disabled={busy} className="btn btn-primary w-full">
          {busy ? "Adding…" : "Add it"}
        </button>
      </form>
          <p className="mt-6 text-center text-xs text-(--color-ink-faint)">
        Have a Pool Nerd export file?{" "}
        <Link to="/import" className="underline">
          Import it instead
        </Link>
        .
      </p>
    </HouseholdShell>
  );
}
